import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Spinner, Alert } from "react-bootstrap";
import { FaArrowRight, FaCalendarAlt, FaClock, FaPenNib } from "react-icons/fa";

import api from "../api/axios";
import CustomNavbar from "../components/CustomNavbar";
import Footer from "../components/Footer";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api
      .get("/blogs")
      .then(({ data }) => active && setBlogs(data))
      .catch(
        () =>
          active &&
          setError("Unable to load articles right now. Please try again later.")
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const [featured, ...rest] = blogs;

  return (
    <>
      <CustomNavbar />

      <style>{`
        .blg {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;
        }

        .blg-hero {
          position: relative; overflow: hidden;
          padding: clamp(52px, 7vw, 84px) 0;
          background:
            radial-gradient(42rem 24rem at 86% 6%, rgba(255,107,53,.30), transparent 62%),
            linear-gradient(135deg, #ff9066 0%, #f3c09f 48%, #fff8f5 100%);
          color: #0f172a;
        }
        .blg-hero__blob {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(80px); opacity: .5;
          width: 24rem; height: 24rem; bottom: -9rem; left: -8rem;
          background: radial-gradient(circle, #fff3ec, transparent 70%);
        }
        .blg-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: #ffffffdd; color: var(--peach-ink);
          padding: 9px 22px; border-radius: 999px;
          font-weight: 700; font-size: .78rem; letter-spacing: .1em;
          box-shadow: 0 8px 20px rgba(194,65,12,.16);
        }
        .blg-hero__title {
          margin-top: 22px; font-weight: 800; letter-spacing: -.02em;
          font-size: clamp(1.9rem, 4.6vw, 3.1rem); line-height: 1.14;
        }
        .blg-hero__title em {
          font-style: normal;
          background: linear-gradient(96deg, #c2410c, #ff6b35 60%, #b91c1c);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .blg-hero__lead {
          max-width: 720px; margin: 18px auto 0; color: #4a3a33;
          font-size: clamp(1rem, 1.7vw, 1.08rem); line-height: 1.85;
        }

        /* Ends white, leaving room for the footer's overlapping CTA panel */
        .blg-body {
          padding: clamp(48px, 6vw, 76px) 0 clamp(92px, 10vw, 130px);
          background: linear-gradient(180deg, #fff8f5 0%, #ffffff 45%, #ffffff 100%);
        }

        /* --- Featured --- */
        .blg-featured {
          position: relative; overflow: hidden; display: block;
          text-decoration: none; border-radius: 24px;
          padding: clamp(28px, 3.6vw, 44px);
          background:
            radial-gradient(30rem 18rem at 88% 6%, rgba(255,107,53,.26), transparent 62%),
            linear-gradient(150deg, #131f36 0%, #0f172a 55%, #0b1120 100%);
          box-shadow: 0 26px 55px rgba(15,23,42,.3);
          transition: transform .26s ease, box-shadow .26s ease;
        }
        .blg-featured__tag {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #0f172a; padding: 8px 18px; border-radius: 999px;
          font-weight: 700; font-size: .74rem; letter-spacing: .1em;
        }
        .blg-featured__title {
          margin-top: 20px; color: #fff; font-weight: 800;
          font-size: clamp(1.45rem, 3vw, 2.2rem); line-height: 1.22;
          letter-spacing: -.02em;
        }
        .blg-featured__text {
          color: #cbd5e1; line-height: 1.9; margin-top: 14px;
          max-width: 780px; font-size: 1rem;
        }
        .blg-featured__more {
          display: inline-flex; align-items: center; gap: 10px; margin-top: 22px;
          color: var(--peach); font-weight: 700;
        }
        .blg-featured__more svg { transition: transform .24s ease; }

        /* --- Meta --- */
        .blg-meta {
          display: flex; flex-wrap: wrap; align-items: center; gap: 14px;
          margin-top: 16px; font-size: .82rem;
        }
        .blg-meta span { display: inline-flex; align-items: center; gap: 6px; }
        .blg-featured .blg-meta { color: #94a3b8; }
        .blg-featured .blg-meta svg { color: var(--peach); }

        /* --- Grid --- */
        .blg-h2 {
          margin-top: 18px; color: #0f172a; font-weight: 800;
          font-size: clamp(1.6rem, 3.2vw, 2.4rem); letter-spacing: -.02em;
        }
        .blg-card {
          position: relative; overflow: hidden; height: 100%;
          display: flex; flex-direction: column;
          padding: 26px 24px; border-radius: 20px; text-decoration: none;
          background: #fff; border: 1px solid #f0e4dd;
          box-shadow: 0 6px 22px rgba(194,65,12,.07);
          transition: transform .26s ease, box-shadow .26s ease, border-color .26s ease;
        }
        .blg-card::before {
          content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft));
          transform: scaleX(0); transform-origin: left;
          transition: transform .34s cubic-bezier(.22,1,.36,1);
        }
        .blg-card__cat {
          align-self: flex-start;
          background: var(--peach-tint); color: var(--peach-ink);
          padding: 5px 13px; border-radius: 999px;
          font-size: .72rem; font-weight: 700;
        }
        .blg-card__title {
          color: #0f172a; font-weight: 700; font-size: 1.14rem;
          line-height: 1.35; margin: 14px 0 10px;
        }
        .blg-card__text {
          color: #64748b; line-height: 1.75; font-size: .93rem; margin-bottom: 16px;
          /* Keeps every card's summary to a consistent depth */
          display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .blg-card .blg-meta { color: #94a3b8; margin: 0 0 14px; }
        .blg-card .blg-meta svg { color: var(--peach); }
        .blg-card__more {
          margin-top: auto; display: inline-flex; align-items: center; gap: 8px;
          color: var(--peach-ink); font-weight: 700; font-size: .9rem;
        }
        .blg-card__more svg { transition: transform .26s ease; }

        /* A mouse hovers; a finger can't. On touch, lib/touchMotion.js adds
           .is-live as the card scrolls through the middle of the screen. */
        .blg-featured:hover,
        .blg-featured.is-live {
          transform: translateY(-6px); box-shadow: 0 32px 64px rgba(15,23,42,.42);
        }
        .blg-featured:hover .blg-featured__more svg,
        .blg-featured.is-live .blg-featured__more svg { transform: translateX(5px); }
        .blg-card:hover,
        .blg-card.is-live {
          transform: translateY(-7px);
          box-shadow: 0 22px 46px rgba(194,65,12,.18);
          border-color: var(--peach-soft);
        }
        .blg-card:hover::before,
        .blg-card.is-live::before { transform: scaleX(1); }
        .blg-card:hover .blg-card__more svg,
        .blg-card.is-live .blg-card__more svg { transform: translateX(5px); }
        @media (prefers-reduced-motion: reduce) {
          .blg-card, .blg-card::before, .blg-featured { transition: none; }
          .blg-card:hover, .blg-featured:hover { transform: none; }
        }

        .blg-empty {
          text-align: center; padding: 48px 20px; border-radius: 20px;
          background: #fff8f5; border: 1px dashed #ffd4c2; color: #94a3b8;
        }
      `}</style>

      <div className="blg">
        {/* Hero */}
        <section className="blg-hero">
          <span className="blg-hero__blob" />
          <Container className="text-center" style={{ position: "relative", zIndex: 1 }}>
            <span className="blg-badge" data-aos="fade-up">
              <FaPenNib />
              INSIGHTS &amp; BLOG
            </span>

            {/* theme.css hides <br> in section headings below 576px — the
                explicit space stops the halves concatenating. */}
            <h1 className="blg-hero__title" data-aos="fade-up" data-aos-delay="60">
              Ideas, Insights &amp;
              <br />{" "}
              <em>Industry Trends</em>
            </h1>

            <p className="blg-hero__lead" data-aos="fade-up" data-aos-delay="120">
              Explore expert perspectives on technology, digital transformation,
              project delivery, and business innovation.
            </p>
          </Container>
        </section>

        <section className="blg-body">
          <Container>
            {loading && (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "#ff6b35" }} />
              </div>
            )}

            {!loading && error && (
              <Alert variant="danger" className="text-center">
                {error}
              </Alert>
            )}

            {!loading && !error && blogs.length === 0 && (
              <div className="blg-empty">
                No articles published yet. Check back soon.
              </div>
            )}

            {/* Featured — the whole card is the link, so it's one tab stop */}
            {featured && (
              <Link to={`/blog/${featured._id}`} className="blg-featured" data-aos="fade-up" data-live>
                <span className="blg-featured__tag">FEATURED ARTICLE</span>
                <h2 className="blg-featured__title">{featured.title}</h2>

                <div className="blg-meta">
                  {featured.category && <span>{featured.category}</span>}
                  <span>
                    <FaCalendarAlt />
                    {formatDate(featured.createdAt)}
                  </span>
                  {featured.readTime && (
                    <span>
                      <FaClock />
                      {featured.readTime}
                    </span>
                  )}
                </div>

                <p className="blg-featured__text">{featured.description}</p>

                <span className="blg-featured__more">
                  Read Full Article <FaArrowRight />
                </span>
              </Link>
            )}

            {/* `rest`, not `blogs` — the featured post was rendered twice before */}
            {rest.length > 0 && (
              <>
                <div className="text-center mt-5 mb-4" data-aos="fade-up">
                  <span className="blg-badge">LATEST ARTICLES</span>
                  <h2 className="blg-h2">Latest Insights</h2>
                </div>

                <Row className="g-4">
                  {rest.map((blog, i) => (
                    <Col
                      lg={4}
                      md={6}
                      key={blog._id}
                      data-aos="fade-up"
                      data-aos-delay={(i % 3) * 90}
                    >
                      <Link to={`/blog/${blog._id}`} className="blg-card" data-live>
                        {blog.category && (
                          <span className="blg-card__cat">{blog.category}</span>
                        )}

                        <h3 className="blg-card__title">{blog.title}</h3>

                        <div className="blg-meta">
                          <span>
                            <FaCalendarAlt />
                            {formatDate(blog.createdAt)}
                          </span>
                          {blog.readTime && (
                            <span>
                              <FaClock />
                              {blog.readTime}
                            </span>
                          )}
                        </div>

                        <p className="blg-card__text">{blog.description}</p>

                        <span className="blg-card__more">
                          Read more <FaArrowRight />
                        </span>
                      </Link>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </Container>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default Blog;
